import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);
    
    // Store credentials in sessionStorage for Basic Auth
    const credentials = btoa(`${username}:${password}`);
    sessionStorage.setItem("adminAuth", credentials);
    
    // Test credentials by fetching stats
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: {
          "Authorization": `Basic ${credentials}`
        }
      });
      
      if (response.ok) {
        toast.success("Login successful! ✨");
        navigate("/admin/dashboard");
      } else {
        sessionStorage.removeItem("adminAuth");
        toast.error("Invalid credentials");
      }
    } catch (error) {
      sessionStorage.removeItem("adminAuth");
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-mystic-void flex items-center justify-center px-6 stars-bg"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_crystal-haven-2/artifacts/y9uaa7nd_FullLogo_Transparent.png" 
              alt="Krystalz" 
              className="h-40 w-auto brightness-125 contrast-110"
            />
          </div>
          <p className="text-mystic-mist/70 flex items-center justify-center gap-2">
            <Moon className="w-4 h-4" />
            Admin Portal
          </p>
        </div>

        <div className="glass rounded-2xl p-8 border border-white/10 neon-glow-hover">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-6 rounded-full bg-mystic-amethyst/20 border border-mystic-amethyst/30">
            <Lock className="w-6 h-6 text-mystic-amethyst" />
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-mystic-silver">
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="bg-black/40 border-white/10 text-mystic-silver placeholder:text-mystic-mist/30 focus:border-mystic-amethyst rounded-lg"
                data-testid="admin-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-mystic-silver">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-black/40 border-white/10 text-mystic-silver placeholder:text-mystic-mist/30 focus:border-mystic-amethyst rounded-lg"
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full py-6 text-base font-serif italic neon-glow"
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : "Enter Portal"}
            </Button>
          </form>

          <p className="text-center text-mystic-mist/50 text-sm mt-6">
            Default: admin / crystal@123
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
